import { wccModules } from 'enums/wccModules';
import { inject, injectable } from 'inversify';
import { Subscribable, SubscribableOrNullableValue } from 'knockout';
import { CurrentUserManager } from 'managers/currentUser';
import DiscussionManager from 'managers/discussion';
import { IWCCStorageManager } from 'managers/iStorage';
import PostsFilteringTagsManager from 'managers/tags/postsFiltering';
import { withEffect } from 'mixins/withEffect';
import { EffectsContainer } from 'mixins/withEffects';
import CommentsFilters from 'models/commentsFilters';
import { Tag } from 'models/tag';
import './commentsFilters.html';
import './commentsFilters.scss';
import TopicCommentFilterVM, { TopicCommentsFilterVMConfig } from './filter';
import { TopicCommentsFiltersVMTagsFilterConfig } from './tagsFilter';
import { TopicCommentsFiltersVMTextFilterConfig } from './textFilter';

components
    .registerLocal('topic-comments-text-filter', () => require('./textFilter'))
    .registerLocal('topic-comments-tags-filter', () => require('./tagsFilter'))
    .registerLocal('topic-comments-filter-header', () => require('./filterHeader'))
    .preload('sliding-container', 'tag-picker');

export interface TopicCommentsFiltersVMConfig {
    filters: SubscribableOrNullableValue<CommentsFilters>
}

@injectable()
export default class TopicCommentsFiltersVM {
    labels = labels;

    filtersConfigs = ko.observableArray<TopicCommentsFilterVMConfig>();
    filters = ko.observableArray<TopicCommentFilterVM>();

    searchString: Subscribable<string | undefined>
    activeFiltersCount: Subscribable<number>

    isActive = ko.observable(false);
    isInactive = this.isActive.not();

    hasChanges: Subscribable<boolean>
    hasActiveFilters: Subscribable<boolean>
    allowToToggle: Subscribable<boolean>

    constructor(
        @inject(wccModules.componentConfig) config: TopicCommentsFiltersVMConfig,
        @inject(wccModules.componentInfo) info: ko.components.ComponentInfo,
        @inject(wccModules.storage) storage: IWCCStorageManager,
        @inject(wccModules.effects) effects: EffectsContainer
    ) {
        const filtersObj = ko.flattenComputed(config.filters);

        this.searchString = filtersObj.mapNotNull({
            read: filters => filters.searchString(),
            write: (filters, value) => filters.searchString(value)
        });

        const userManager = storage.get(CurrentUserManager, { });
        const user = userManager.pluck(m => m.person);
        const userReady = user.isNotNull();

        const discussionManager = storage.get(DiscussionManager, {});
        const discussion = discussionManager.pluck(m => m.discussion);
        const discussionReady = discussion.isNotNull();

        const postsFilteringType = ko.pureComputed(() => {
            switch (user()?.status()) {
                case enums.MembershipStatusTypes.Moderator.value: return discussion()?.postsFilteringTypeForModerators();
                case enums.MembershipStatusTypes.Observer.value: return discussion()?.postsFilteringTypeForObservers();
                case enums.MembershipStatusTypes.Member.value: return discussion()?.postsFilteringTypeForParticipants();
            }
        }).default(enums.PostsFilteringTypes.NoFiltering.value);

        const peopleTags = ko.observableArray<Tag>();
        const loadingPeopleTags = ko.observable(false);

        const isReady = ko.pureComputed(() => userReady() && discussionReady() && !loadingPeopleTags());
        const isVisible = ko.pureComputed(() => isReady() && this.filtersConfigs().length > 0).extend({ deferred: true });

        this.activeFiltersCount = this.filters.reduce((result, filter) => filter.isActive() ? result + 1 : result, () => 0);
        this.hasActiveFilters = this.activeFiltersCount.is(v => v > 0);

        this.hasChanges = this.filters.any(f => f.hasChanges());
        const filtersHaveChangesOrActive = this.filters.any(f => f.hasChanges() || f.isActive());
        this.allowToToggle = ko.pureComputed(() => !filtersHaveChangesOrActive());

        effects.register((filtersHaveChangesOrActive) => {
            if (filtersHaveChangesOrActive)
                this.isActive(true);
        }, [filtersHaveChangesOrActive]);

        effects.register((isReady, filtersObj, user, discussion, peopleTags) => {
            if (isReady && filtersObj != undefined && user != undefined && discussion != undefined) {
                const filtersConfigs = <Array<TopicCommentsFilterVMConfig>>[];

                if (postsFilteringType() & enums.PostsFilteringTypes.FilterByText.value) {
                    filtersConfigs.push(<TopicCommentsFiltersVMTextFilterConfig>{
                        title: labels.ContentTextSearch,
                        icon: 'far fa-comment-alt-lines',
                        type: 'topic-comments-text-filter',
                        value: filtersObj.searchString,
                        placeholder: labels.SearchContent
                    });
                }

                if (postsFilteringType() & enums.PostsFilteringTypes.FilterByTags.value) {
                    filtersConfigs.push(<TopicCommentsFiltersVMTagsFilterConfig>{
                        title: labels.PersonTags,
                        icon: 'far fa-user',
                        type: 'topic-comments-tags-filter',
                        value: filtersObj.peopleTagsIDs,
                        tags: peopleTags
                    });
                }

                this.filtersConfigs(filtersConfigs);
            } else {
                this.filtersConfigs([]);
            }
        }, [isReady, filtersObj, user, discussion, peopleTags]);

        //load posts filtering tags
        effects.register(user => {
            peopleTags([]);
            loadingPeopleTags(true);

            //wait for user. we need to know user status
            if (user != undefined) {
                const postsFilteringTagsManager = new PostsFilteringTagsManager(<string>settings.discussionId, <number>user.status());

                return [
                    postsFilteringTagsManager, //dispose manager on component dispose

                    withEffect((loading, tags) => {
                        peopleTags(loading ? [] : tags); //load tags into component array
                        loadingPeopleTags(loading);
                    }, [postsFilteringTagsManager.loading, postsFilteringTagsManager.tags])
                ];
            }
        }, [user]);

        ko.applyBindingsToNode(info.element, {
            'visible': isVisible,

            'css': {
                'wcc-not-selectable': true,
                'topic-comments-filters': true,
                'topic-comments-filters--toggable': this.allowToToggle,
                'topic-comments-filters--active': this.isActive,
                'topic-comments-filters--inactive': this.isInactive
            }
        });
    }

    toggle = () => {
        if (this.allowToToggle())
            this.isActive.toggle();
    }    

    applyChanges = () => {
        this.filters().forEach(f => f.applyChanges());
    }

    reset = () => {
        this.filters().forEach(f => {
            f.reset();
            f.applyChanges();
        });
    }
}